import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type TransportTab   = 'overview' | 'routes' | 'vehicles' | 'drivers' | 'allocations' | 'attendance';
export type VehicleStatus  = 'Active' | 'Inactive' | 'Under Maintenance' | 'Retired';
export type DriverStatus   = 'Active' | 'On Leave' | 'Inactive';
export type RouteStatus    = 'Active' | 'Inactive' | 'Suspended';
export type AllocStatus    = 'Active' | 'Inactive' | 'On Leave';
export type ShiftType      = 'Morning' | 'Afternoon' | 'Both';
export type FuelType       = 'Diesel' | 'CNG' | 'Petrol' | 'Electric';
export type ViewMode       = 'list' | 'form' | 'detail';
export type AttSubTab      = 'mark' | 'records';
export type BoardingStatus = 'Boarded' | 'Not Boarded' | 'Leave';

export interface RouteStop {
  stop_no:         number;
  stop_name:       string;
  landmark:        string;
  distance:        number;   // km from school
  pickup_time:     string;
  drop_time:       string;
}

export interface Route {
  id:              number;
  route_code:      string;
  route_name:      string;
  description:     string;
  vehicle_id:      number | null;
  driver_id:       number | null;
  shift:           ShiftType;
  total_stops:     number;
  total_distance:  number;   // km
  morning_start:   string;
  afternoon_start: string;
  status:          RouteStatus;
  stops:           RouteStop[];
  student_count:   number;
  route_fee:       number;
  created_at:      string;
}

export interface Vehicle {
  id:              number;
  vehicle_no:      string;
  vehicle_name:    string;
  vehicle_type:    string;
  make:            string;
  model:           string;
  model_year:      number | null;
  capacity:        number;
  fuel_type:       FuelType;
  engine_no:       string;
  chassis_no:      string;
  insurance_expiry:string;
  fitness_expiry:  string;
  permit_expiry:   string;
  last_service:    string;
  next_service:    string;
  odometer:        number | null;
  gps_device_id:   string;
  status:          VehicleStatus;
  photo:           string;
  remarks:         string;
  created_at:      string;
}

export interface Driver {
  id:              number;
  emp_code:        string;
  driver_name:     string;
  photo:           string;
  driver_dob:      string;
  mobile:          string;
  alt_mobile:      string;
  email:           string;
  address:         string;
  license_no:      string;
  license_type:    string;
  license_expiry:  string;
  experience_years:   number;
  assigned_routeid:   number | null;
  assigned_vehicleid: number | null;
  aadhar_no:       string;
  joining_date:    string;
  status:          DriverStatus;
  remarks:         string;
}

export interface Allocation {
  id:              number;
  student_id:      any;
  student_name:    string;
  class_name:      string;
  roll_no:         string;
  route_id:        number;
  stop_name:       string;
  shift:           ShiftType;
  start_date:      string;
  end_date:        string;
  route_fee:       any;
  status:          AllocStatus;
  remarks:         string;
}

export interface TransportAttRow {
  alloc_id:      number;
  student_id:    string;
  student_name:  string;
  class_name:    string;
  roll_no:       string;
  route_id:      number;
  stop_name:     string;
  shift:         ShiftType;
  morning:       BoardingStatus | null;
  afternoon:     BoardingStatus | null;
  remark:        string;
}

export interface TransportAttRecord extends TransportAttRow {
  id:        number;
  date:      string;
  marked_by: string;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

@Component({
  selector:    'app-transport',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './transport.html',
  styleUrl:    './transport.scss',
})
export class Transport implements OnInit {
  dataService = inject(DataService);
  loading = false;

  activeTab       = signal<TransportTab>('overview');
  saveSuccess     = signal('');
  showDeleteModal = signal(false);
  deleteCtx       = signal<{ label: string; id: number; type: string } | null>(null);

  // Route state
  routeMode       = signal<ViewMode>('list');
  editingRouteId  = signal<number | null>(null);
  routeSubmitted  = false;
  routeForm: Partial<Route> = {};
  stopForm: Partial<RouteStop> = {};

  // Vehicle state
  vehicleMode       = signal<ViewMode>('list');
  editingVehicleId  = signal<number | null>(null);
  vehicleSubmitted  = false;
  vehicleForm: Partial<Vehicle> = {};
  vehicleSearch     = '';
  vehicleStatusFilter = '';

  // Driver state
  driverMode        = signal<ViewMode>('list');
  editingDriverId   = signal<number | null>(null);
  driverSubmitted   = false;
  driverForm: Partial<Driver> = {};
  driverSearch      = '';

  // Allocation state
  allocMode         = signal<ViewMode>('list');
  editingAllocId    = signal<number | null>(null);
  allocSubmitted    = false;
  allocForm: Partial<Allocation> = {};
  allocSearch       = '';
  allocRoute        = 0;
  allocStatus       = '';

  // Attendance state
  attSubTab         = signal<AttSubTab>('mark');
  attDate           = signal(todayIso());
  attRoute          = signal(0);
  attSearch         = signal('');
  attRows           = signal<TransportAttRow[]>([]);
  attSaved          = signal(false);
  attRecDate        = signal(todayIso());
  attRecRoute       = signal(0);
  attRecStatus      = signal('');
  attRecSearch      = signal('');
  attRecTrip        = signal<'all' | 'Morning' | 'Afternoon'>('all');

  readonly boardingStatuses: BoardingStatus[] = ['Boarded', 'Not Boarded', 'Leave'];

  // ── LOOKUPS ───────────────────────────────────────────
  readonly shiftTypes:   ShiftType[]  = ['Morning','Afternoon','Both'];
  readonly fuelTypes:    FuelType[]   = ['Diesel','CNG','Petrol','Electric'];
  readonly vehicleTypes  = ['School Bus (Large)','School Bus (Mini)','Van','Tempo Traveller','Auto'];
  readonly licenseTypes  = ['LMV','HMV','HGV','MGV','LMV-TR'];
  readonly classOptions  = this.dataService.classes;
  readonly stopList      = ['School Gate','Sector 7 Market','Dwarka Sector 10','Rohini Sector 3','Pitampura','Janakpuri West','Vikaspuri','Uttam Nagar','Dwarka Sector 1','Civil Lines','Malviya Nagar','Lajpat Nagar','Defence Colony','Kalkaji','Saket','Vasant Kunj'];

  // ── DATA ─────────────────────────────────────────────
  private nR = 4; private nV = 5; private nD = 5; private nA = 9; private nAtt = 1;

  routes   = signal<Route[]> ([]);
  vehicles = signal<Vehicle[]> ([]);
  drivers  = signal<Driver[]> ([]);
  allocs   = signal<Allocation[]>([]);
  attRecords = signal<TransportAttRecord[]>(this.buildAttRecords());

  // ── COMPUTED ─────────────────────────────────────────
  readonly stats = computed(() => ({
    routes:    this.routes().length,
    vehicles:  this.vehicles().length,
    drivers:   this.drivers().length,
    students:  this.allocs().filter(a => a.status === 'Active').length,
    activeRoutes:   this.routes().filter(r => r.status === 'Active').length,
    activeVehicles: this.vehicles().filter(v => v.status === 'Active').length,
    activeDrivers:  this.drivers().filter(d => d.status === 'Active').length,
    maintenance:    this.vehicles().filter(v => v.status === 'Under Maintenance').length,
    onLeave:        this.drivers().filter(d => d.status === 'On Leave').length,
    expiringInsurance: this.vehicles().filter(v => v.insurance_expiry && new Date(v.insurance_expiry) < new Date(Date.now() + 30*86400000)).length,
    expiringLicense:   this.drivers().filter(d => d.license_expiry && new Date(d.license_expiry) < new Date(Date.now() + 30*86400000)).length,
  }));

  // readonly filteredVehicles = computed(() => {
  //   const q = this.vehicleSearch.toLowerCase();
  //   return this.vehicles().filter(v =>
  //     (!q || [v.vehicle_no,v.vehicle_name,v.make,v.model].some(x => x.toLowerCase().includes(q))) &&
  //     (!this.vehicleStatusFilter || v.status === this.vehicleStatusFilter)
  //   );
  // });
  filteredVehicles(): Vehicle[] {
    const q = this.vehicleSearch.toLowerCase();
    
    return this.vehicles().filter(v => 
      (!q || [v.vehicle_no, v.vehicle_name, v.make, v.model].some(x => x?.toLowerCase().includes(q))) && 
      (!this.vehicleStatusFilter || v.status === this.vehicleStatusFilter)
    );
  }


  // readonly filteredDrivers = computed(() => {
  //   const q = this.driverSearch.toLowerCase();
  //   return this.drivers().filter(d =>
  //     !q || [d.driver_name,d.emp_code,d.mobile,d.license_no].some(x => x.toLowerCase().includes(q))
  //   );
  // });
  filteredDrivers(): Driver[] {
    const q = this.driverSearch.toLowerCase();
    
    return this.drivers().filter(d =>
      !q || [d.driver_name, d.emp_code, d.mobile, d.license_no].some(x => x?.toLowerCase().includes(q))
    );
  }

  // readonly filteredAllocs = computed(() => {
  //   const q = this.allocSearch.toLowerCase();
  //   return this.allocs().filter(a =>
  //     (!q || [a.student_name,a.class_name].some(x => x.toLowerCase().includes(q))) &&
  //     (!this.allocRoute || a.route_id === this.allocRoute) &&
  //     (!this.allocStatus || a.status === this.allocStatus)
  //   );
  // });
  filteredAllocs(): Allocation[] {
    const q = this.allocSearch.toLowerCase();
    
    return this.allocs().filter(a =>
      (!q || [a.student_name, a.student_id.toString(), a.class_name].some(x => x?.toLowerCase().includes(q))) &&
      (!this.allocRoute || a.route_id === this.allocRoute) &&
      (!this.allocStatus || a.status === this.allocStatus)
    );
  }

  readonly filteredAttRows = computed(() => {
    const q = this.attSearch().toLowerCase().trim();
    if (!q) return this.attRows();
    return this.attRows().filter(r =>
      r.student_name.toLowerCase().includes(q) ||
      r.student_id.toLowerCase().includes(q) ||
      r.roll_no.toLowerCase().includes(q) ||
      r.class_name.toLowerCase().includes(q) ||
      r.stop_name.toLowerCase().includes(q)
    );
  });

  readonly attMarkSummary = computed(() => this.summarizeRows(this.attRows()));

  readonly filteredAttRecords = computed(() => {
    const q = this.attRecSearch().toLowerCase().trim();
    const date = this.attRecDate();
    const route = Number(this.attRecRoute());
    const status = this.attRecStatus();
    const trip = this.attRecTrip();
    return this.attRecords()
      .filter(r => {
        const matchDate  = !date || r.date === date;
        const matchRoute = !route || r.route_id === route;
        const matchSearch = !q ||
          [r.student_name, r.student_id, r.class_name, r.roll_no, r.stop_name]
            .some(x => x.toLowerCase().includes(q));
        const matchStatus = !status || this.rowMatchesStatus(r, status, trip);
        return matchDate && matchRoute && matchSearch && matchStatus;
      })
      .sort((a, b) => a.student_name.localeCompare(b.student_name) || a.date.localeCompare(b.date));
  });

  readonly attRecordSummary = computed(() => this.summarizeRows(this.filteredAttRecords()));

  readonly todayAttStats = computed(() => {
    const today = todayIso();
    const recs = this.attRecords().filter(r => r.date === today);
    const summary = this.summarizeRows(recs);
    return { ...summary, marked: recs.length, date: today };
  });

  readonly todayAttRecords = computed(() => {
    const today = todayIso();
    return this.attRecords()
      .filter(r => r.date === today)
      .sort((a, b) => a.student_name.localeCompare(b.student_name));
  });

  readonly todayByRoute = computed(() => {
    const today = todayIso();
    return this.routes().map(route => {
      const recs = this.attRecords().filter(r => r.date === today && r.route_id === route.id);
      return { route, ...this.summarizeRows(recs), marked: recs.length };
    }).filter(x => x.marked > 0 || x.route.status === 'Active');
  });

  readonly expiryAlerts = computed(() => {
    const alerts: { type: string; label: string; expiry: string; icon: string }[] = [];
    const soon = new Date(Date.now() + 60*86400000);
    this.vehicles().forEach(v => {
      if (v.insurance_expiry && new Date(v.insurance_expiry) < soon)
        alerts.push({ type:'insurance', label:`${v.vehicle_no} Insurance`, expiry: v.insurance_expiry, icon:'bi-shield-exclamation' });
      if (v.fitness_expiry && new Date(v.fitness_expiry) < soon)
        alerts.push({ type:'fitness', label:`${v.vehicle_no} Fitness Cert`, expiry: v.fitness_expiry, icon:'bi-clipboard2-x' });
      if (v.next_service && new Date(v.next_service) < soon)
        alerts.push({ type:'service', label:`${v.vehicle_no} Service Due`, expiry: v.next_service, icon:'bi-tools' });
    });
    this.drivers().forEach(d => {
      if (d.license_expiry && new Date(d.license_expiry) < soon)
        alerts.push({ type:'license', label:`${d.driver_name} License`, expiry: d.license_expiry, icon:'bi-card-text' });
    });
    return alerts.sort((a,b) => a.expiry.localeCompare(b.expiry));
  });

  ngOnInit() {
    this.loadAttRows();

    this.loading = true;
    this.dataService.getAllRecords('trpt_routes').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          res[i].stops = JSON.parse(res[i].stops);
          this.routes.set(res);
        }
      }
    });

    this.loading = true;
    this.dataService.getAllRecords('trpt_vehicles').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.vehicles.set(res);
      }
    });

    this.loading = true;
    this.dataService.getAllRecords('trpt_drivers').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.drivers.set(res);
      }
    });

    this.loading = true;
    this.dataService.getAllRecords('trpt_alloc').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.allocs.set(res);
      }
    });

  }

  // ── ROUTE CRUD ────────────────────────────────────────
  openNewRoute(): void {
    this.routeForm = { route_code:'', route_name:'', description:'', vehicle_id:null, driver_id:null, shift:'Morning', total_stops:0, total_distance:0, morning_start:'07:00', afternoon_start:'14:00', status:'Active', stops:[], student_count:0, route_fee:0 };
    this.editingRouteId.set(null); this.routeSubmitted = false;
    this.routeMode.set('form');
  }

  editRoute(r: Route): void {
    this.routeForm = JSON.parse(JSON.stringify(r));
    this.editingRouteId.set(r.id); this.routeSubmitted = false;
    this.routeMode.set('form');
  }

  viewRouteDetail(r: Route): void { this.editingRouteId.set(r.id); this.routeMode.set('detail'); }

  addStop(): void {
    const stops = (this.routeForm.stops ?? []);
    stops.push({ stop_no: stops.length+1, stop_name:'', landmark:'', distance:0, pickup_time:'', drop_time:'' });
    this.routeForm.stops = stops;
  }

  removeStop(i: number): void { this.routeForm.stops?.splice(i,1); }

  saveRoute(): void {
    this.routeSubmitted = true;
    if (!this.routeForm.route_code || !this.routeForm.route_name) return;
    const now = new Date().toISOString().substring(0,10);
    const route: Route = {
      id: this.editingRouteId() ?? this.nR++,
      route_code:     this.routeForm.route_code!,
      route_name:     this.routeForm.route_name!,
      description:    this.routeForm.description ?? '',
      vehicle_id:     this.routeForm.vehicle_id ?? null,
      driver_id:      this.routeForm.driver_id ?? null,
      shift:          this.routeForm.shift ?? 'Morning',
      total_stops:    this.routeForm.stops?.length ?? 0,
      total_distance: this.routeForm.total_distance ?? 0,
      morning_start:  this.routeForm.morning_start ?? '07:00',
      afternoon_start:this.routeForm.afternoon_start ?? '14:00',
      status:         this.routeForm.status ?? 'Active',
      stops:          this.routeForm.stops ?? [],
      student_count:  this.routeForm.student_count ?? 0,
      route_fee:      this.routeForm.route_fee ?? 0,
      created_at:     now,
    };

    //  const route: Route = { id:1, route_code:'RT-01', route_name:'North Campus Route',    description:'Covers Rohini, Pitampura, Dwarka sectors', vehicle_id:1, driver_id:1, shift:'Both', total_stops:7, total_distance:22, morning_start:'07:00', afternoon_start:'14:00', status:'Active', student_count:42, route_fee:2500, stops:[{stop_no:1,stop_name:'School Gate',     landmark:'Main Gate',           distance:0, pickup_time:'07:00',drop_time:'15:00'},{stop_no:2,stop_name:'Dwarka Sector 10',landmark:'Metro Station Gate 2',  distance:4, pickup_time:'07:15',drop_time:'14:45'},{stop_no:3,stop_name:'Dwarka Sector 7', landmark:'Near Community Centre', distance:5.5,pickup_time:'07:20',drop_time:'14:40'},{stop_no:4,stop_name:'Rohini Sector 3', landmark:'Near Sector 3 Market',  distance:10,pickup_time:'07:30',drop_time:'14:30'},{stop_no:5,stop_name:'Pitampura',       landmark:'TV Tower Road',          distance:14,pickup_time:'07:40',drop_time:'14:20'}], created_at:'2025-04-01' };

    let myRoutes: any = Object.assign({}, route);
    myRoutes.stops = JSON.stringify(myRoutes.stops);

    if (this.editingRouteId() !== null) {
      this.doUpdate('trpt_routes', myRoutes);
      this.routes.update(l => l.map(r => r.id === route.id ? route : r));
    } else {
      this.doSave('trpt_routes', myRoutes);
      this.routes.update(l => [...l, route]);
    }

    this.routeMode.set('list'); this.flash('Route saved!');
  }

  confirmDelete(type: string, id: number, label: string): void {
    this.deleteCtx.set({ type, id, label }); this.showDeleteModal.set(true);
  }

  executeDelete(): void {
    const ctx = this.deleteCtx();
    if (!ctx) return;
    if (ctx.type === 'route') {
      this.doDelete('trpt_routes', ctx.id);
      this.routes.update(l => l.filter(r => r.id !== ctx.id));
    }
    if (ctx.type === 'vehicle') {
      this.doDelete('trpt_vehicles', ctx.id);
      this.vehicles.update(l => l.filter(v => v.id !== ctx.id));
    }
    if (ctx.type === 'driver') {
      this.doDelete('trpt_drivers', ctx.id);
      this.drivers.update(l => l.filter(d => d.id !== ctx.id));
    }
    if (ctx.type === 'alloc') {
      this.doDelete('trpt_alloc', ctx.id);
      this.allocs.update(l => l.filter(a => a.id !== ctx.id));
    }

    this.showDeleteModal.set(false); 
    this.deleteCtx.set(null);
    this.flash('Deleted successfully.');
  }

  toggleRouteStatus(r: Route): void { this.routes.update(l => l.map(x => x.id === r.id ? {...x, status: x.status === 'Active' ? 'Inactive' : 'Active'} : x)); }

  // ── VEHICLE CRUD ──────────────────────────────────────
  openNewVehicle(): void { this.vehicleForm = { status:'Active', fuel_type:'Diesel', vehicle_type:'School Bus (Large)' }; this.editingVehicleId.set(null); this.vehicleSubmitted = false; this.vehicleMode.set('form'); }
  editVehicle(v: Vehicle): void { this.vehicleForm = {...v}; this.editingVehicleId.set(v.id); this.vehicleSubmitted = false; this.vehicleMode.set('form'); }
  viewVehicleDetail(v: Vehicle): void { this.editingVehicleId.set(v.id); this.vehicleMode.set('detail'); }

  saveVehicle(): void {
    this.vehicleSubmitted = true;
    if (!this.vehicleForm.vehicle_no) return;
    const v: Vehicle = { ...this.vehicleForm as Vehicle, id: this.editingVehicleId() ?? this.nV++, created_at: new Date().toISOString().substring(0,10) };
    // const v: Vehicle = { id:1, vehicle_no:'DL 01 AB 1234', vehicle_name:'Bus 1 - North',   vehicle_type:'School Bus (Large)',   make:'Tata',         model:'StarBus',    model_year:2020, capacity:52, fuel_type:'Diesel', engine_no:'ENG-TAT-001', chassis_no:'CHS-TAT-001', insurance_expiry:'2026-11-30', fitness_expiry:'2026-08-15', permit_expiry:'2026-12-31', last_service:'2026-05-01', next_service:'2026-08-01', odometer:48500, gps_device_id:'GPS-001', status:'Active',  photo:'', remarks:'', created_at:'2025-04-01' }

    if (this.editingVehicleId() !== null) {
      this.doUpdate('trpt_vehicles', v);
      this.vehicles.update(l => l.map(x => x.id === v.id ? v : x));
    } else {
      this.doSave('trpt_vehicles', v);
      this.vehicles.update(l => [...l, v]);
    }

    this.vehicleMode.set('list'); this.flash('Vehicle saved!');
  }

  toggleVehicleStatus(v: Vehicle): void { this.vehicles.update(l => l.map(x => x.id === v.id ? {...x, status: x.status === 'Active' ? 'Inactive' : 'Active'} : x)); }

  // ── DRIVER CRUD ───────────────────────────────────────
  openNewDriver(): void { this.driverForm = { status:'Active', experience_years:0 }; this.editingDriverId.set(null); this.driverSubmitted = false; this.driverMode.set('form'); }
  editDriver(d: Driver): void { this.driverForm = {...d}; this.editingDriverId.set(d.id); this.driverSubmitted = false; this.driverMode.set('form'); }

  saveDriver(): void {
    this.driverSubmitted = true;
    if (!this.driverForm.driver_name || !this.driverForm.mobile) return;
    const d: Driver = { ...this.driverForm as Driver, id: this.editingDriverId() ?? this.nD++, emp_code: this.driverForm.emp_code ?? `DRV-${String(this.nD).padStart(3,'0')}` };
    // const d: Driver = { id:1, emp_code:'DRV-001', driver_name:'Deepak Yadav',   photo:'', driver_dob:'1985-03-15', mobile:'9889012345', alt_mobile:'', email:'deepak@edusmart.in',   address:'House 22, Dwarka Sector 7, Delhi',       license_no:'DL-0120185012345', license_type:'HGV', license_expiry:'2027-06-30', experience_years:12, assigned_routeid:1, assigned_vehicleid:1, status:'Active',    aadhar_no:'2345678901234', joining_date:'2020-04-01', remarks:'' };

    if (this.editingDriverId() !== null) {
      this.doUpdate('trpt_drivers', d);
      this.drivers.update(l => l.map(x => x.id === d.id ? d : x));
    } else {
      this.doSave('trpt_drivers', d);
      this.drivers.update(l => [...l, d]);
    }

    this.driverMode.set('list'); this.flash('Driver saved!');
  }

  toggleDriverStatus(d: Driver): void { this.drivers.update(l => l.map(x => x.id === d.id ? {...x, status: x.status === 'Active' ? 'Inactive' : 'Active'} : x)); }

  // ── ALLOCATION CRUD ───────────────────────────────────
  openNewAlloc(): void { this.allocForm = { status:'Active', shift:'Morning', start_date: new Date().toISOString().substring(0,10) }; this.editingAllocId.set(null); this.allocSubmitted = false; this.allocMode.set('form'); }
  editAlloc(a: Allocation): void { this.allocForm = {...a}; this.editingAllocId.set(a.id); this.allocSubmitted = false; this.allocMode.set('form'); }

  onAllocRouteChange(): void {
    const r = this.routes().find(r => r.id === Number(this.allocForm.route_id));
    if (r) this.allocForm.route_fee = r.route_fee;
  }

  saveAlloc(): void {
    this.allocSubmitted = true;
    if (!this.allocForm.student_id || !this.allocForm.route_id) return;
    const a: Allocation = { ...this.allocForm as Allocation, id: this.editingAllocId() ?? this.nA++ };
    // const a: Allocation = { id:1, student_id: 1, student_name:'Arjun Sharma',   class_name:'Class XI - Science', roll_no:'11-A-01', route_id:1, stop_name:'Dwarka Sector 10', shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:'2500.00', status:'Active',   remarks:'' };

    if (this.editingAllocId() !== null) {
      this.doUpdate('trpt_alloc', a);
      this.allocs.update(l => l.map(x => x.id === a.id ? a : x));
    } else {
      this.doSave('trpt_alloc', a);
      this.allocs.update(l => [...l, a]);
    }

    this.allocMode.set('list'); this.flash('Allocation saved!');
  }

  toggleAllocStatus(a: Allocation): void { this.allocs.update(l => l.map(x => x.id === a.id ? {...x, status: x.status === 'Active' ? 'Inactive' : 'Active'} : x)); }

  resetAllocFilters(): void {
    this.allocSearch = '';
    this.allocRoute = 0;
    this.allocStatus = '';
  }

  // ── DAILY ATTENDANCE ──────────────────────────────────
  setAttTab(tab: TransportTab): void {
    this.activeTab.set(tab);
    this.routeMode.set('list');
    this.vehicleMode.set('list');
    this.driverMode.set('list');
    this.allocMode.set('list');
    if (tab === 'attendance') this.loadAttRows();
  }

  onAttDateChange(date: string): void {
    this.attDate.set(date);
    this.loadAttRows();
  }

  onAttRouteChange(routeId: number | string): void {
    this.attRoute.set(Number(routeId) || 0);
    this.loadAttRows();
  }

  loadAttRows(): void {
    const date = this.attDate();
    const routeId = Number(this.attRoute());
    const active = this.allocs().filter(a =>
      a.status === 'Active' && (!routeId || a.route_id === routeId)
    );
    const existing = new Map(
      this.attRecords().filter(r => r.date === date).map(r => [r.student_id, r])
    );
    this.attRows.set(active.map((a: any) => {
      const prev = existing.get(a.student_id);
      return {
        alloc_id:     a.id,
        student_id:   a.student_id,
        student_name: a.student_name,
        class_name:   a.class_name,
        roll_no:      a.roll_no,
        route_id:     a.route_id,
        stop_name:    a.stop_name,
        shift:        a.shift,
        morning:      a.shift === 'Afternoon' ? null : (prev?.morning ?? 'Boarded'),
        afternoon:    a.shift === 'Morning'   ? null : (prev?.afternoon ?? 'Boarded'),
        remark:       prev?.remark ?? '',
      };
    }));
    this.attSearch.set('');
    this.attSaved.set(false);
  }

  setBoarding(studentId: string, trip: 'Morning' | 'Afternoon', status: BoardingStatus): void {
    this.attRows.update(rows => rows.map(r => {
      if (r.student_id !== studentId) return r;
      if (trip === 'Morning' && r.morning === null) return r;
      if (trip === 'Afternoon' && r.afternoon === null) return r;
      return trip === 'Morning' ? { ...r, morning: status } : { ...r, afternoon: status };
    }));
    this.attSaved.set(false);
  }

  setAllBoarding(trip: 'Morning' | 'Afternoon' | 'Both', status: BoardingStatus): void {
    this.attRows.update(rows => rows.map(r => ({
      ...r,
      morning:   (trip === 'Afternoon' || r.morning === null) ? r.morning : status,
      afternoon: (trip === 'Morning'   || r.afternoon === null) ? r.afternoon : status,
    })));
    this.attSaved.set(false);
  }

  saveAttendance(): void {
    const date = this.attDate();
    const rows = this.attRows();
    if (!rows.length) return;
    const ids = new Set(rows.map(r => r.student_id));
    const saved: TransportAttRecord[] = rows.map(r => ({
      ...r,
      id: this.nAtt++,
      date,
      marked_by: 'Admin User',
    }));
    this.attRecords.update(list => [
      ...saved,
      ...list.filter(r => !(r.date === date && ids.has(r.student_id))),
    ]);
    this.attSaved.set(true);
    this.attRecDate.set(date);
    const used = rows.filter(r => r.morning === 'Boarded' || r.afternoon === 'Boarded').length;
    this.flash(`Attendance saved for ${this.formatDate(date)} · ${used} of ${rows.length} used transport`);
  }

  resetAttRecordFilters(): void {
    this.attRecDate.set(todayIso());
    this.attRecRoute.set(0);
    this.attRecStatus.set('');
    this.attRecSearch.set('');
    this.attRecTrip.set('all');
  }

  usedTransport(r: { morning: BoardingStatus | null; afternoon: BoardingStatus | null }): boolean {
    return r.morning === 'Boarded' || r.afternoon === 'Boarded';
  }

  getBoardingClass(s: BoardingStatus | null): string {
    if (s === 'Boarded') return 'bd-boarded';
    if (s === 'Not Boarded') return 'bd-missed';
    if (s === 'Leave') return 'bd-leave';
    return 'bd-na';
  }

  getDay(iso: string): string {
    if (!iso) return '—';
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' });
  }

  todayIso(): string { return todayIso(); }

  private rowMatchesStatus(
    r: TransportAttRecord,
    status: string,
    trip: 'all' | 'Morning' | 'Afternoon',
  ): boolean {
    const legs: (BoardingStatus | null)[] =
      trip === 'Morning' ? [r.morning] :
      trip === 'Afternoon' ? [r.afternoon] :
      [r.morning, r.afternoon];
    if (status === 'Boarded') return legs.includes('Boarded');
    if (status === 'Not Boarded') return legs.includes('Not Boarded') && !legs.includes('Boarded');
    if (status === 'Leave') return legs.includes('Leave');
    return true;
  }

  private summarizeRows(rows: { morning: BoardingStatus | null; afternoon: BoardingStatus | null }[]): {
    total: number; boarded: number; notBoarded: number; onLeave: number; morningBoarded: number; afternoonBoarded: number;
  } {
    return {
      total: rows.length,
      boarded: rows.filter(r => this.usedTransport(r)).length,
      notBoarded: rows.filter(r =>
        (r.morning === 'Not Boarded' || r.afternoon === 'Not Boarded') &&
        r.morning !== 'Boarded' && r.afternoon !== 'Boarded' &&
        r.morning !== 'Leave' && r.afternoon !== 'Leave'
      ).length,
      onLeave: rows.filter(r => r.morning === 'Leave' || r.afternoon === 'Leave').length,
      morningBoarded: rows.filter(r => r.morning === 'Boarded').length,
      afternoonBoarded: rows.filter(r => r.afternoon === 'Boarded').length,
    };
  }

  // ── HELPERS ───────────────────────────────────────────
  getRouteName(id: number | null): string { return this.routes().find(r => r.id === id)?.route_name ?? '—'; }
  getVehicleNo(id: number | null): string { return this.vehicles().find(v => v.id === id)?.vehicle_no ?? '—'; }
  getDriverName(id: number | null): string { return this.drivers().find(d => d.id === id)?.driver_name ?? '—'; }
  getViewRoute(): Route | undefined { return this.routes().find(r => r.id === this.editingRouteId()); }
  getViewVehicle(): Vehicle | undefined { return this.vehicles().find(v => v.id === this.editingVehicleId()); }

  isExpiringSoon(date: string): boolean { return !!date && new Date(date) < new Date(Date.now() + 30*86400000); }
  isExpired(date: string): boolean { return !!date && new Date(date) < new Date(); }

  getDocStatus(date: string): 'valid'|'expiring'|'expired' {
    if (!date) return 'valid';
    if (this.isExpired(date)) return 'expired';
    if (this.isExpiringSoon(date)) return 'expiring';
    return 'valid';
  }

  getVehicleStatusClass(s: VehicleStatus): string { return {Active:'vs-active','Inactive':'vs-inactive','Under Maintenance':'vs-maintenance',Retired:'vs-retired'}[s]??''; }
  getDriverStatusClass(s: DriverStatus):  string { return {Active:'ds-active','On Leave':'ds-leave',Inactive:'ds-inactive'}[s]??''; }
  getRouteStatusClass(s: RouteStatus):   string { return {Active:'rs-active',Inactive:'rs-inactive',Suspended:'rs-suspended'}[s]??''; }
  getAllocStatusClass(s: AllocStatus):   string { return {Active:'as-active',Inactive:'as-inactive','On Leave':'as-leave'}[s]??''; }

  formatCurrency(n: number): string { return new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(n); }
  formatDate(iso: string): string { if(!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }
  daysUntil(iso: string): number { return Math.ceil((new Date(iso).getTime()-Date.now())/86400000); }
  getInitials(name: string): string { return name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase(); }

  private flash(m: string): void { this.saveSuccess.set(m); setTimeout(()=>this.saveSuccess.set(''),2500); }

  // ── SEED DATA ─────────────────────────────────────────
  // private buildRoutes(): Route[] {
  //   return [
  //     { id:1, route_code:'RT-01', route_name:'North Campus Route',    description:'Covers Rohini, Pitampura, Dwarka sectors', vehicle_id:1, driver_id:1, shift:'Both', total_stops:7, total_distance:22, morning_start:'07:00', afternoon_start:'14:00', status:'Active', student_count:42, monthly_fee:2500,
  //       stops:[{stop_no:1,stop_name:'School Gate',     landmark:'Main Gate',           distance:0, pickup_time:'07:00',drop_time:'15:00'},{stop_no:2,stop_name:'Dwarka Sector 10',landmark:'Metro Station Gate 2',  distance:4, pickup_time:'07:15',drop_time:'14:45'},{stop_no:3,stop_name:'Dwarka Sector 7', landmark:'Near Community Centre', distance:5.5,pickup_time:'07:20',drop_time:'14:40'},{stop_no:4,stop_name:'Rohini Sector 3', landmark:'Near Sector 3 Market',  distance:10,pickup_time:'07:30',drop_time:'14:30'},{stop_no:5,stop_name:'Pitampura',       landmark:'TV Tower Road',          distance:14,pickup_time:'07:40',drop_time:'14:20'}],
  //       created_at:'2025-04-01' },
  //     { id:2, route_code:'RT-02', route_name:'South Delhi Route',     description:'Malviya Nagar, Saket, Vasant Kunj', vehicle_id:2, driver_id:2, shift:'Both', total_stops:6, total_distance:18, morning_start:'07:10', afternoon_start:'14:00', status:'Active', student_count:35, monthly_fee:2200,
  //       stops:[{stop_no:1,stop_name:'School Gate',     landmark:'Main Gate',           distance:0, pickup_time:'07:10',drop_time:'15:00'},{stop_no:2,stop_name:'Malviya Nagar',   landmark:'Near Metro Station',    distance:8, pickup_time:'07:25',drop_time:'14:45'},{stop_no:3,stop_name:'Saket',           landmark:'Saket District Centre', distance:11,pickup_time:'07:35',drop_time:'14:35'},{stop_no:4,stop_name:'Vasant Kunj',     landmark:'Vasant Kunj Sector B',  distance:16,pickup_time:'07:45',drop_time:'14:25'}],
  //       created_at:'2025-04-01' },
  //     { id:3, route_code:'RT-03', route_name:'East Sector Route',     description:'Laxmi Nagar, Mayur Vihar, Preet Vihar', vehicle_id:3, driver_id:3, shift:'Morning', total_stops:5, total_distance:15, morning_start:'07:05', afternoon_start:'14:05', status:'Active', student_count:28, monthly_fee:2000,
  //       stops:[{stop_no:1,stop_name:'School Gate',     landmark:'Main Gate',           distance:0, pickup_time:'07:05',drop_time:'15:05'},{stop_no:2,stop_name:'Preet Vihar',     landmark:'Preet Vihar Metro',     distance:5, pickup_time:'07:20',drop_time:'14:50'},{stop_no:3,stop_name:'Mayur Vihar',     landmark:'Phase 1 Market',        distance:9, pickup_time:'07:30',drop_time:'14:40'},{stop_no:4,stop_name:'Laxmi Nagar',     landmark:'Laxmi Nagar Metro',     distance:13,pickup_time:'07:40',drop_time:'14:30'}],
  //       created_at:'2025-04-01' },
  //     { id:4, route_code:'RT-04', route_name:'Janakpuri Express',     description:'Janakpuri, Vikaspuri, Uttam Nagar', vehicle_id:4, driver_id:4, shift:'Both', total_stops:4, total_distance:12, morning_start:'07:15', afternoon_start:'14:10', status:'Inactive', student_count:0, monthly_fee:1800,
  //       stops:[{stop_no:1,stop_name:'School Gate',     landmark:'Main Gate',           distance:0, pickup_time:'07:15',drop_time:'15:00'},{stop_no:2,stop_name:'Janakpuri',       landmark:'Janakpuri West Metro',  distance:6, pickup_time:'07:30',drop_time:'14:45'},{stop_no:3,stop_name:'Uttam Nagar',     landmark:'Near Market',           distance:10,pickup_time:'07:40',drop_time:'14:35'}],
  //       created_at:'2025-04-01' },
  //   ];
  // }

  // private buildVehicles(): Vehicle[] {
  //   return [
  //     { id:1, vehicle_no:'DL 01 AB 1234', vehicle_name:'Bus 1 - North',   vehicle_type:'School Bus (Large)',   make:'Tata',         model:'StarBus',    model_year:2020, capacity:52, fuel_type:'Diesel', engine_no:'ENG-TAT-001', chassis_no:'CHS-TAT-001', insurance_expiry:'2026-11-30', fitness_expiry:'2026-08-15', permit_expiry:'2026-12-31', last_service:'2026-05-01', next_service:'2026-08-01', odometer:48500, gps_device_id:'GPS-001', status:'Active',  photo:'', remarks:'', created_at:'2025-04-01' },
  //     { id:2, vehicle_no:'DL 02 CD 5678', vehicle_name:'Bus 2 - South',   vehicle_type:'School Bus (Large)',   make:'Ashok Leyland',model:'Lynx',       model_year:2019, capacity:48, fuel_type:'CNG',    engine_no:'ENG-AL-002',  chassis_no:'CHS-AL-002',  insurance_expiry:'2026-09-30', fitness_expiry:'2026-10-20', permit_expiry:'2026-09-30', last_service:'2026-04-10', next_service:'2026-07-10', odometer:62000, gps_device_id:'GPS-002', status:'Active',  photo:'', remarks:'', created_at:'2025-04-01' },
  //     { id:3, vehicle_no:'DL 03 EF 9012', vehicle_name:'Bus 3 - East',    vehicle_type:'School Bus (Mini)',    make:'Force',        model:'Traveller',  model_year:2022, capacity:26, fuel_type:'Diesel', engine_no:'ENG-FRC-003', chassis_no:'CHS-FRC-003', insurance_expiry:'2026-08-10', fitness_expiry:'2026-09-05', permit_expiry:'2026-11-15', last_service:'2026-06-01', next_service:'2026-09-01', odometer:21000, gps_device_id:'GPS-003', status:'Active',  photo:'', remarks:'', created_at:'2025-04-01' },
  //     { id:4, vehicle_no:'DL 04 GH 3456', vehicle_name:'Bus 4 - West',    vehicle_type:'School Bus (Large)',   make:'Tata',         model:'Winger',     model_year:2018, capacity:50, fuel_type:'Diesel', engine_no:'ENG-TAT-004', chassis_no:'CHS-TAT-004', insurance_expiry:'2026-08-05', fitness_expiry:'2027-01-10', permit_expiry:'2026-10-31', last_service:'2026-03-15', next_service:'2026-07-15', odometer:88000, gps_device_id:'GPS-004', status:'Under Maintenance', photo:'', remarks:'Annual servicing in progress', created_at:'2025-04-01' },
  //     { id:5, vehicle_no:'DL 05 IJ 7890', vehicle_name:'Van - Staff',     vehicle_type:'Van',                  make:'Maruti',       model:'Eeco',       model_year:2021, capacity:7,  fuel_type:'CNG',    engine_no:'ENG-MAR-005', chassis_no:'CHS-MAR-005', insurance_expiry:'2027-02-28', fitness_expiry:'2027-03-15', permit_expiry:'2027-02-28', last_service:'2026-07-01', next_service:'2026-10-01', odometer:15000, gps_device_id:'GPS-005', status:'Active',  photo:'', remarks:'Staff vehicle', created_at:'2025-04-01' },
  //   ];
  // }

  // private buildDrivers(): Driver[] {
  //   return [
  //     { id:1, emp_code:'DRV-001', driver_name:'Deepak Yadav',   photo:'', driver_dob:'1985-03-15', mobile:'9889012345', alt_mobile:'', email:'deepak@edusmart.in',   address:'House 22, Dwarka Sector 7, Delhi',       license_no:'DL-0120185012345', license_type:'HGV', license_expiry:'2027-06-30', experience_years:12, assigned_routeid:1, assigned_vehicleid:1, status:'Active',    aadhar_no:'2345678901234', joining_date:'2020-04-01', remarks:'' },
  //     { id:2, emp_code:'DRV-002', driver_name:'Mohit Sharma',   photo:'', driver_dob:'1982-07-22', mobile:'9867543211', alt_mobile:'', email:'mohit@edusmart.in',    address:'Flat 5B, Saket Apartments, New Delhi',   license_no:'DL-0220175034567', license_type:'HGV', license_expiry:'2026-09-15', experience_years:15, assigned_routeid:2, assigned_vehicleid:2, status:'Active',    aadhar_no:'3456789012345', joining_date:'2019-08-01', remarks:'' },
  //     { id:3, emp_code:'DRV-003', driver_name:'Rajan Kumar',    photo:'', driver_dob:'1990-01-10', mobile:'9845678902', alt_mobile:'', email:'rajan@edusmart.in',    address:'C-14, Laxmi Nagar, East Delhi',          license_no:'DL-0320195045678', license_type:'HGV', license_expiry:'2026-08-20', experience_years:8,  assigned_routeid:3, assigned_vehicleid:3, status:'Active',    aadhar_no:'4567890123456', joining_date:'2021-06-01', remarks:'' },
  //     { id:4, emp_code:'DRV-004', driver_name:'Suresh Babu',    photo:'', driver_dob:'1978-11-05', mobile:'9878901235', alt_mobile:'', email:'suresh@edusmart.in',   address:'Plot 33, Janakpuri West, Delhi',         license_no:'DL-0420105056789', license_type:'HMV', license_expiry:'2027-03-31', experience_years:18, assigned_routeid:4, assigned_vehicleid:4, status:'On Leave',  aadhar_no:'5678901234567', joining_date:'2018-01-15', remarks:'Medical leave until Aug 10' },
  //     { id:5, emp_code:'DRV-005', driver_name:'Vikas Singh',    photo:'', driver_dob:'1993-05-20', mobile:'9856712346', alt_mobile:'', email:'vikas@edusmart.in',    address:'H-7, Rohini Sector 3, Delhi',            license_no:'DL-0520215067890', license_type:'LMV', license_expiry:'2027-12-15', experience_years:3,  assigned_routeid:5, assigned_vehicleid:5, status:'Active',    aadhar_no:'6789012345678', joining_date:'2023-03-01', remarks:'Staff van driver' },
  //   ];
  // }

  private buildAllocs(): Allocation[] {
    return [
      { id:1, student_id:'S001', student_name:'Arjun Sharma',   class_name:'Class XI - Science', roll_no:'11-A-01', route_id:1, stop_name:'Dwarka Sector 10', shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2500, status:'Active',   remarks:'' },
      { id:2, student_id:'S002', student_name:'Priya Mehta',    class_name:'Class XI - Science', roll_no:'11-A-02', route_id:1, stop_name:'Rohini Sector 3',  shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2500, status:'Active',   remarks:'' },
      { id:3, student_id:'S003', student_name:'Rahul Kumar',    class_name:'Class IX',           roll_no:'9-A-01',  route_id:2, stop_name:'Malviya Nagar',    shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2200, status:'Active',   remarks:'' },
      { id:4, student_id:'S005', student_name:'Vikram Singh',   class_name:'Class IX',           roll_no:'9-A-02',  route_id:3, stop_name:'Preet Vihar',      shift:'Morning',   start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2000, status:'Active',   remarks:'' },
      { id:5, student_id:'S006', student_name:'Kavita Yadav',   class_name:'Class XI - Science', roll_no:'11-A-03', route_id:1, stop_name:'Pitampura',        shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2500, status:'Active',   remarks:'' },
      { id:6, student_id:'S009', student_name:'Pooja Nair',     class_name:'BCA',                roll_no:'BCA-03',  route_id:2, stop_name:'Saket',            shift:'Both',      start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2200, status:'Active',   remarks:'' },
      { id:7, student_id:'S010', student_name:'Suresh Babu S.', class_name:'Class IX',           roll_no:'9-B-12',  route_id:3, stop_name:'Mayur Vihar',      shift:'Morning',   start_date:'2025-04-01', end_date:'2026-03-31', route_fee:2000, status:'Inactive', remarks:'Discontinued' },
      { id:8, student_id:'S013', student_name:'Meena Sharma',   class_name:'Class XI - Science', roll_no:'11-A-04', route_id:2, stop_name:'Malviya Nagar',    shift:'Both',      start_date:'2025-07-01', end_date:'2026-03-31', route_fee:2200, status:'Active',   remarks:'' },
    ];
  }

  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

  onSearchStaff(): void {
    this.dataService.getSingleRecord('staff', this.driverForm.emp_code).then((res: any) => {
      if(res) {
        const staff: any = res;

        this.driverForm.driver_name = staff.first_name + ' ' + staff.last_name;
        this.driverForm.driver_dob = staff.dob;
        this.driverForm.joining_date = staff.doj;
        this.driverForm.mobile = staff.mobile;
        this.driverForm.email = staff.email;
        this.driverForm.address = staff.address + ', ' + staff.city + ' - ' + staff.pin_code + ', ' + staff.state;

      }
    });
  }

  onSearchStd(): void {
    this.dataService.getSingleRecord('admissions', this.allocForm.student_id).then((res: any) => {
      if(res) {
        const std: any = res;

        this.allocForm.student_name = std.std_name;
        // this.allocForm.class_name = std.class
        // this.allocForm.roll_no = srd.roll_no

      }
    });
  }

  private shiftOffset(iso: string, days: number): string {
    const dt = new Date(iso + 'T00:00:00');
    dt.setDate(dt.getDate() - days);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private attRec(
    date: string,
    a: Allocation,
    morning: BoardingStatus | null,
    afternoon: BoardingStatus | null,
    remark = '',
  ): TransportAttRecord {
    return {
      id: this.nAtt++,
      date,
      alloc_id: a?.id || 0,
      student_id: a?.student_id.toString(),
      student_name: a?.student_name,
      class_name: a?.class_name,
      roll_no: a?.roll_no,
      route_id: a?.route_id,
      stop_name: a?.stop_name,
      shift: a?.shift,
      morning: a?.shift === 'Afternoon' ? null : morning,
      afternoon: a?.shift === 'Morning' ? null : afternoon,
      remark,
      marked_by: 'Admin User',
    };
  }

  private buildAttRecords(): TransportAttRecord[] {
    const today = todayIso();
    const yest = this.shiftOffset(today, 1);
    const earlier = this.shiftOffset(today, 3);
    const allocs: any = this.buildAllocs().filter(a => a.status === 'Active');
    // const allocs: any = this.allocs();
    const byId = (id: string) => allocs.find((a: any) => a.student_id.toString() === id)!;
    return [
      this.attRec(today,   byId('S001'), 'Boarded',     'Boarded'),
      this.attRec(today,   byId('S002'), 'Boarded',     'Not Boarded', 'Left early with parent'),
      this.attRec(today,   byId('S003'), 'Boarded',     'Boarded'),
      this.attRec(today,   byId('S005'), 'Not Boarded', null,          'Did not board morning bus'),
      this.attRec(today,   byId('S006'), 'Leave',       'Leave',       'Sick leave'),
      this.attRec(today,   byId('S009'), 'Boarded',     'Boarded'),
      this.attRec(today,   byId('S013'), 'Boarded',     'Boarded'),
      this.attRec(yest,    byId('S001'), 'Boarded',     'Boarded'),
      this.attRec(yest,    byId('S002'), 'Boarded',     'Boarded'),
      this.attRec(yest,    byId('S003'), 'Not Boarded', 'Not Boarded', 'Absent from school'),
      this.attRec(yest,    byId('S005'), 'Boarded',     null),
      this.attRec(yest,    byId('S006'), 'Boarded',     'Boarded'),
      this.attRec(yest,    byId('S009'), 'Boarded',     'Leave',       'Afternoon tuition'),
      this.attRec(yest,    byId('S013'), 'Boarded',     'Boarded'),
      this.attRec(earlier, byId('S001'), 'Boarded',     'Boarded'),
      this.attRec(earlier, byId('S002'), 'Boarded',     'Boarded'),
      this.attRec(earlier, byId('S003'), 'Boarded',     'Boarded'),
      this.attRec(earlier, byId('S005'), 'Boarded',     null),
      this.attRec(earlier, byId('S006'), 'Boarded',     'Not Boarded'),
      this.attRec(earlier, byId('S009'), 'Not Boarded', 'Not Boarded', 'Parent drop-off'),
      this.attRec(earlier, byId('S013'), 'Boarded',     'Boarded'),
    ];
  }
}
